/**
 * AIFederatedDialog - "Ask your data" for cross-source federated widgets.
 *
 * User types a natural-language question. Backend LLM plans a federated query
 * (subqueries + combine SQL) and dry-runs it. On accept the plan is applied to
 * the parent FederatedEditor state.
 */
import React, { useState } from "react";
import { Sparkles, Loader2, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";

export function AIFederatedDialog({ open, onOpenChange, datasets, onApply }) {
  const [question, setQuestion] = useState("");
  const [pickedIds, setPickedIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const toggle = (id) => {
    setPickedIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const run = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const { data } = await api.post("/ai/federated-sql", {
        question, dataset_ids: pickedIds,
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "AI planning failed");
    } finally { setBusy(false); }
  };

  const apply = () => {
    if (!result?.plan) return;
    onApply(result.plan);
    onOpenChange(false);
    setQuestion(""); setPickedIds([]); setResult(null); setError(null);
  };

  const canAsk = question.trim().length > 5 && pickedIds.length >= 1 && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[color:var(--brand-mint)]" />
            AI Assist — cross-source query
          </DialogTitle>
          <DialogDescription>
            Describe the answer you want. The AI will pick the right datasets, measures, and generate a DuckDB combine SQL.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs text-[color:var(--ink-3)]">Datasets to consider (pick 2 or more)</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-1.5 max-h-40 overflow-auto rounded-lg border border-[color:var(--glass-border)] p-2">
              {datasets.map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer hover:text-[color:var(--ink)]">
                  <Checkbox
                    data-testid={`ai-fed-ds-${d.id}`}
                    checked={pickedIds.includes(d.id)}
                    onCheckedChange={() => toggle(d.id)}
                  />
                  <span className="truncate">{d.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-[color:var(--ink-3)]">Question</Label>
            <Textarea
              data-testid="ai-fed-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="mt-1.5 h-24 bg-[color:var(--surface)] text-sm"
              placeholder="e.g. Compare CRM pipeline to ERP revenue by region and show conversion rate"
            />
          </div>

          {result && (
            <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 space-y-2">
              <div className={"text-[11px] flex items-center gap-1 " + (result.validated ? "text-[color:var(--success)]" : "text-[color:var(--danger)]")}>
                {result.validated ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                {result.validated
                  ? `Plan is valid — ${(result.columns || []).length} column(s): ${(result.columns || []).join(", ")}`
                  : `Plan did not execute cleanly: ${result.error}`}
              </div>
              {result.repaired && result.repaired.length > 0 && (
                <div data-testid="ai-fed-repaired" className="rounded-md bg-[color:rgba(43,78,255,0.06)] border border-[color:var(--brand-ocean)]/30 p-2">
                  <div className="text-[10px] text-[color:var(--brand-ocean)] font-medium mb-1 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />Auto-repaired {result.repaired.length} reference(s)
                  </div>
                  <ul className="text-[10px] font-mono space-y-0.5 text-[color:var(--ink-2)]">
                    {result.repaired.slice(0, 6).map((r, i) => (
                      <li key={i}>
                        <span className="text-[color:var(--ink-3)]">{r.where}:</span>{" "}
                        <span className="text-[color:var(--danger)] line-through">{r.from}</span> → <span className="text-[color:var(--success)]">{r.to}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-[10px] text-[color:var(--ink-3)]">Subqueries:</div>
              <ul className="text-[11px] font-mono text-[color:var(--ink-2)]">
                {(result.plan.subqueries || []).map((s) => (
                  <li key={s.alias}>
                    <span className="text-[color:var(--brand-ocean)]">{s.alias}</span>
                    {"  "}
                    {s.semantic_query
                      ? `measures=[${(s.semantic_query.measures || []).join(",")}] group_by=[${(s.semantic_query.dimensions || []).map((d) => d.key).join(",")}]`
                      : "adhoc spec"}
                  </li>
                ))}
              </ul>
              <div className="text-[10px] text-[color:var(--ink-3)]">Combine SQL:</div>
              <pre data-testid="ai-fed-sql" className="text-[10px] font-mono bg-[color:var(--surface)] p-2 rounded max-h-40 overflow-auto whitespace-pre-wrap">
                {result.plan.combine_sql}
              </pre>
            </div>
          )}

          {error && (
            <div className="text-[11px] text-[color:var(--danger)] flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />{error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-1.5">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {!result && (
            <Button data-testid="ai-fed-generate" onClick={run} disabled={!canAsk}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Generate plan
            </Button>
          )}
          {result && (
            <Button data-testid="ai-fed-apply" onClick={apply}>
              Apply to editor <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
